"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import type { Variants } from "framer-motion";
import type { CaseItem } from "@/lib/contentful-types";
import { Placeholder } from "@/components/taller/placeholder";
import { TallerButtonLink } from "@/components/taller/button";
import { useTranslations } from "@/i18n/client";

interface Props {
  casos: CaseItem[];
}

// Generador de color simple basado en el string para que cada categoría tenga su tono
const getCategoryColor = (str: string) => {
  if (str === "todos") return "var(--color-accent)";
  const colors = ["#04D4CF", "#FF9F5A", "#8A4FFF", "#4ADE80", "#F43F5E", "#3B82F6"];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const EASE = [0.22, 1, 0.36, 1] as [number, number, number, number];

// Helper to clean raw Contentful markdown markers for the list view
function cleanText(text: string) {
  return text
    .replace(/__([^_\n]+?)__[:]?/g, "$1:") // "__Label__" o "__Label:__" -> "Label:"
    .replace(/\*\*([^*]+)\*\*/g, "$1")       // "**bold**" -> "bold"
    .replace(/^[-*+]\s+/gm, "")               // Eliminar bullets de listas al inicio de línea
    .replace(/\n+/g, " ")                     // Convertir saltos de línea en espacios
    .trim();
}

const cardVariants: Variants = {
  hidden:  { opacity: 0, y: 36 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.07, duration: 0.55, ease: EASE },
  }),
};

export default function CasosClientPage({ casos }: Props) {
  const { t, isLoading } = useTranslations();
  const ce = t("casosExito");
  const [activeFilter, setActiveFilter] = useState<string>("todos");

  if (isLoading) return null;

  // Sacamos los tipos únicos que realmente existen en la data de Contentful
  const tiposExistentes = Array.from(
    new Set(casos.map((c) => c.tipo).filter(Boolean)),
  ).sort();
  const sectoresCount = tiposExistentes.length;

  // Mapeamos los filtros para que sean 100% dinámicos
  const FILTER_LABELS = [
    { value: "todos", label: ce.filters.todos || "Todos" },
    ...tiposExistentes.map((tipo) => ({
      value: tipo,
      label: tipo.charAt(0).toUpperCase() + tipo.slice(1),
    })),
  ];

  const filtered =
    activeFilter === "todos"
      ? casos
      : casos.filter((c) => c.tipo === activeFilter);

  return (
    <>
      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden max-w-[var(--container-max)] mx-auto px-6 md:px-14 pt-[72px] pb-16">
        {/* subtle grid bg */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(rgba(4,212,207,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(4,212,207,0.04) 1px,transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />

        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="text-[11px] font-mono tracking-widest uppercase text-[color:var(--color-mute)] relative"
        >
          {ce.hero.overline}
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="taller-page-title mt-6 relative"
        >
          {ce.hero.h1Part1}
          <em className="not-italic font-bold text-[color:var(--color-accent)]">
            {ce.hero.h1Accent}
          </em>
          .
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
          className="font-sans text-[18px] leading-[1.6] text-[color:var(--color-mute)] mt-8 max-w-[560px] relative"
        >
          {ce.hero.subtitle}
        </motion.p>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-wrap gap-10 mt-12 pt-12 border-t border-[color:var(--color-border)] relative"
        >
          {[
            { num: `${casos.length}`, label: "Proyectos entregados" },
            { num: `${sectoresCount}`, label: "Sectores" },
            { num: "80 %",            label: "Tasa de cierre media" },
          ].map(({ num, label }) => (
            <div key={label}>
              <div className="font-sans font-bold text-[38px] leading-none tracking-tight text-[color:var(--color-ink)]">
                {num}
              </div>
              <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[color:var(--color-mute)] mt-1">
                {label}
              </div>
            </div>
          ))}
        </motion.div>
      </section>

      {/* ── FILTER BAR ───────────────────────────────────────── */}
      <div className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 pb-10">
        <div className="flex gap-2 flex-wrap" role="tablist" aria-label={ce.filters.label}>
          {FILTER_LABELS.map(({ value, label }) => {
            const isActive = activeFilter === value;
            const color = getCategoryColor(value);
            return (
              <button
                key={value}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveFilter(value)}
                className="relative font-mono text-[11px] tracking-[0.14em] uppercase px-4 py-2 rounded-full border cursor-pointer overflow-hidden transition-colors duration-200"
                style={{
                  borderColor: isActive ? color : "var(--color-border)",
                  color: isActive ? "#fff" : "var(--color-mute)",
                }}
              >
                {isActive && (
                  <motion.span
                    layoutId="filter-pill"
                    className="absolute inset-0 rounded-full"
                    style={{ background: color }}
                    transition={{ type: "spring", stiffness: 380, damping: 28 }}
                  />
                )}
                <span className="relative z-10">
                  {label}
                  {value !== "todos" && (
                    <span className="ml-2 opacity-60">
                      {casos.filter((c) => c.tipo === value).length}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── GRID ─────────────────────────────────────────────── */}
      <section className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 pb-28">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeFilter}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filtered.map((item, i) => {
                  const accent = getCategoryColor(item.tipo);
                  const isFeatured = i === 0 && activeFilter === "todos";

                  return (
                    <motion.article
                      key={item.slug}
                      custom={i}
                      variants={cardVariants}
                      initial="hidden"
                      animate="visible"
                      whileHover={{
                        y: -5,
                        transition: { duration: 0.25 },
                      }}
                      className={`group relative flex flex-col rounded-2xl overflow-hidden border border-[color:var(--color-border)] bg-[color:var(--color-surface)] transition-shadow duration-400${isFeatured ? " md:col-span-2" : ""}`}
                      style={
                        {
                          "--card-accent": accent,
                          willChange: "transform",
                        } as React.CSSProperties
                      }
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLElement).style.boxShadow = `0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px ${accent}40`;
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.boxShadow = "";
                      }}
                    >
                      {/* Image area */}
                      <Link
                        href={`/casos-exito/${item.slug}`}
                        aria-label={`${ce.card.view} ${item.client}`}
                        className="relative w-full overflow-hidden flex items-center justify-center no-underline cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-[color:var(--card-accent)]"
                      >
                        {item.imagenPrincipal ? (
                          <div className="relative w-full h-auto" style={{ maxWidth: item.imagenPrincipal.width }}>
                            <Image
                              src={item.imagenPrincipal.url}
                              width={item.imagenPrincipal.width}
                              height={item.imagenPrincipal.height}
                              alt={item.imagenPrincipal.alt || item.client}
                              sizes={isFeatured ? "100vw" : "(max-width:768px) 100vw, 50vw"}
                              className="w-full h-auto object-contain transition-transform duration-700 group-hover:scale-105"
                            />
                          </div>
                        ) : (
                          <div
                            className="absolute inset-0 flex items-center justify-center bg-transparent"
                          >
                            <Placeholder
                              label={item.client}
                              height="100%"
                              width="100%"
                              dark={i % 3 === 2}
                            />
                          </div>
                        )}

                        {/* Gradient overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-surface)] via-transparent to-transparent opacity-70 group-hover:opacity-50 transition-opacity duration-500" />

                        {/* Tag badge */}
                        {item.tag && (
                          <div className="absolute top-4 left-4 z-20">
                            <span
                              className="font-mono text-[9px] tracking-[0.2em] uppercase px-2.5 py-1 rounded-full backdrop-blur-md"
                              style={{
                                background: `${accent}25`,
                                border: `1px solid ${accent}50`,
                                color: accent,
                              }}
                            >
                              {item.tag}
                            </span>
                          </div>
                        )}

                        {/* View case hint */}
                        <span
                          className="absolute left-1/2 bottom-4 z-20 -translate-x-1/2 translate-y-2 whitespace-nowrap font-mono text-[9px] tracking-[0.2em] uppercase px-3 py-1.5 rounded-full backdrop-blur-md opacity-0 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0 group-focus-visible:opacity-100 group-focus-visible:translate-y-0"
                          style={{
                            background: `${accent}25`,
                            border: `1px solid ${accent}50`,
                            color: accent,
                          }}
                        >
                          {ce.card.view}
                        </span>
                      </Link>

                      {/* Content */}
                      <div
                        className={`p-6 flex gap-6 flex-col flex-1 ${isFeatured ? "md:flex-row md:items-end" : ""}`}
                      >
                        <div className="flex-1">
                          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[color:var(--color-mute)] mb-2">
                            {item.kind}
                          </div>
                          <h2
                            className={`font-sans font-bold tracking-tight leading-tight text-[color:var(--color-ink)] m-0 ${isFeatured ? "text-[26px] md:text-[38px]" : "text-[22px] md:text-[26px]"}`}
                          >
                            {item.client}
                          </h2>
                          <p className="font-sans text-[14px] leading-[1.65] text-[color:var(--color-mute)] mt-3 mb-0 line-clamp-3">
                            {cleanText(item.resumen)}
                          </p>
                        </div>

                        <div className={`flex flex-col gap-3 ${isFeatured ? "md:shrink-0" : ""}`}>
                          <div className="flex flex-wrap gap-1.5">
                            {item.stack.slice(0, 3).map((tech) => (
                              <span
                                key={tech}
                                className="font-mono text-[9px] tracking-[0.08em] uppercase rounded px-2 py-0.5 border border-[color:var(--color-border)] text-[color:var(--color-mute)]"
                              >
                                {tech}
                              </span>
                            ))}
                          </div>

                          <Link
                            href={`/casos-exito/${item.slug}`}
                            aria-label={`${ce.card.view} ${item.client}`}
                            className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.16em] uppercase no-underline group/lnk self-start transition-opacity"
                            style={{ color: accent }}
                          >
                            <span
                              className="border-b pb-0.5 group-hover/lnk:opacity-70 transition-opacity"
                              style={{ borderColor: accent }}
                            >
                              {ce.card.view}
                            </span>
                            <span className="transition-transform duration-300 group-hover/lnk:translate-x-1">
                            </span>
                          </Link>
                        </div>
                      </div>
                    </motion.article>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-24">
                <p className="font-mono text-[12px] tracking-[0.18em] uppercase text-[color:var(--color-mute)]">
                  {ce.empty}
                </p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </section>

      {/* ── BOTTOM CTA ───────────────────────────────────────── */}
      <section className="relative overflow-hidden py-28 border-t border-[color:var(--color-border)]">
        {/* ambient glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[320px] rounded-full blur-[120px] opacity-[0.12]"
          style={{ background: "radial-gradient(ellipse,#04D4CF,#8A4FFF)" }}
        />

        <div className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 relative">
          <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-10 items-end">
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="font-sans font-[350] text-[48px] md:text-[64px] leading-[0.96] tracking-[-0.025em] m-0 text-[color:var(--color-ink)]"
            >
              {ce.cta.headingPart1}
              <em className="not-italic font-bold text-[color:var(--color-accent)]">
                {ce.cta.headingAccent}
              </em>
              ?
            </motion.h2>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="font-sans text-[17px] leading-[1.55] text-[color:var(--color-mute)] mt-0 mb-6">
                {ce.cta.subtitle}
              </p>
              <TallerButtonLink variant="primary" href="/contacto">
                {ce.cta.button}
              </TallerButtonLink>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
